import { useState, useRef, useEffect } from "react";
import { ChevronLeftIcon, ChevronRightIcon, DotsVerticalIcon, ExternalLinkIcon, RefreshIcon, TrashIcon, ClipboardCopyIcon, BanIcon, PlayIcon } from "@heroicons/react/outline";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import HeroIcon from "./HeroIcon";
import { Workflow, WorkflowAutomationItem } from "../interfaces";
import CopyToClipboard from "react-copy-to-clipboard";

interface Props {
  currentItem: WorkflowAutomationItem;
  workflow: Workflow;
  prevItemId?: string;
  nextItemId?: string;
  sessionPosition?: number;
  sessionTotal?: number;
  onNavigate: (itemId: string) => void;
  hubspotPortalId?: string;
  workflowId?: string;
  itemId?: string;
  dateFormat?: string;
  isActionRequired?: boolean;
  onRerun?: () => void;
  onDelete?: () => void;
  onRescind?: () => void;
  onRefresh?: () => void;
  canRerun?: boolean;
  canDelete?: boolean;
  canRescind?: boolean;
}

export default function ItemDetailHeader({ currentItem, workflow, prevItemId, nextItemId, sessionPosition, sessionTotal, onNavigate, hubspotPortalId, workflowId, itemId, dateFormat, isActionRequired, onRerun, onDelete, onRescind, onRefresh, canRerun, canDelete, canRescind }: Props) {
  const history = useHistory();
  const { t } = useTranslation();

  const hasSession = sessionPosition !== undefined && sessionTotal !== undefined;

  // Breadcrumb back destination
  const backPath = workflowId ? `/w/${workflowId}/outputs` : "/outputs";

  // Item title: sourceItemName (HubSpot) -> first doc name -> fallback
  const itemTitle = currentItem.sourceItemName
    || currentItem.outputs?.[0]?.onedriveFile?.fileName
    || currentItem.outputs?.[0]?.file?.name
    || currentItem.outputs?.[0]?.filePdf?.name
    || currentItem.outputs?.[0]?.outputName
    || t("Output Details");

  // Runner subheadline
  const runnerName = currentItem.initiatingUser?.fullName ?? currentItem.automationUser?.fullName;
  const createdMoment = moment(currentItem.createdAt);
  const daysSinceCreated = moment().diff(createdMoment, "days");
  const timeLabel = daysSinceCreated < 14
    ? createdMoment.fromNow()
    : createdMoment.format(dateFormat || "DD MMMM YYYY");


  const displayCounter = hasSession
    ? `${sessionPosition} ${t("of")} ${sessionTotal}`
    : null;

  // HubSpot link
  const hubspotUrl = workflow.source?.sourceType === "HUBSPOT" && hubspotPortalId
    ? `https://app.hubspot.com/contacts/${hubspotPortalId}/record/${workflow.source.hubspotConfig?.hubspotObjectTypeId}/${currentItem.sourceItemId}/`
    : undefined;

  // Options menu
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const menuItemClass = "flex items-center font-medium text-xs text-blue w-full px-4 py-2 hover:bg-blue-50 cursor-pointer whitespace-nowrap";

  return (
    <div className="flex items-center w-full bg-white shadow z-10 h-16 flex-shrink-0">
      {/* Left section: breadcrumbs + navigation */}
      <div className="flex items-center flex-1 px-4 min-w-0">
        <div className="flex items-center gap-1 text-sm font-gilroy font-semibold flex-shrink-0">
          <button onClick={() => history.push(backPath)} className="flex items-center gap-1 text-gray-400 hover:text-blue hover:underline">
            <ChevronLeftIcon className="h-5 w-5" />
            {workflow.name}
          </button>
          <span className="text-gray-300">/</span>
          <span className="text-gray-600 truncate max-w-[250px]">
            {itemTitle}
          </span>
        </div>

        {/* Action required flag + navigation counter + arrows */}
        <div className="flex items-center gap-4 flex-shrink-0 ml-auto">
          <div className="flex items-center gap-4">
            {isActionRequired && (
              <span className="font-gilroy text-sm font-semibold text-blue whitespace-nowrap">
                {t("Action Required")}
              </span>
            )}
            {displayCounter && (
              <span className="font-gilroy text-sm font-semibold text-gray-600 whitespace-nowrap tabular-nums min-w-10 text-right">
                {displayCounter}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={`p-0.5 rounded hover:bg-gray-100 ${!prevItemId ? "opacity-20 cursor-not-allowed" : ""}`}
              disabled={!prevItemId}
              onClick={() => prevItemId && onNavigate(prevItemId)}
              aria-label={t("Previous item")}
            >
              <ChevronLeftIcon className="w-6 h-6 text-gray-600" />
            </button>
            <button
              type="button"
              className={`p-0.5 rounded hover:bg-gray-100 ${!nextItemId ? "opacity-20 cursor-not-allowed" : ""}`}
              disabled={!nextItemId}
              onClick={() => nextItemId && onNavigate(nextItemId)}
              aria-label={t("Next item")}
            >
              <ChevronRightIcon className="w-6 h-6 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Right section: sidebar title area */}
      <div className="flex items-center justify-between w-[360px] h-16 flex-shrink-0 border-l border-gray-200 px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="flex items-center justify-center w-9 h-9 text-white rounded flex-shrink-0"
            style={{ backgroundColor: workflow.color }}
          >
            <HeroIcon icon={workflow.icon} className="w-5 h-5" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-gilroy font-semibold text-lg text-black truncate leading-tight">
              {itemTitle}
            </span>
            {runnerName && (
              <span className="text-xs text-gray-500 truncate">
                {t("Ran by")} <span className="font-semibold">{runnerName}</span> · {timeLabel}
              </span>
            )}
          </div>
        </div>
        <div ref={menuRef} className="relative flex-shrink-0 ml-2">
          <button
            type="button"
            onClick={() => setMenuOpen(prev => !prev)}
            className={`flex items-center justify-center p-1 rounded border ${menuOpen ? "bg-gray-100 border-gray-300" : "bg-[#fbfbfb] border-gray-200 hover:bg-gray-100"}`}
          >
            <DotsVerticalIcon className="w-4 h-4 text-gray-400" />
          </button>
          {menuOpen && (
            <div className="absolute mt-1 right-0 z-50 flex flex-col min-w-[160px] bg-white border border-gray-200 rounded overflow-y-auto">
              {onRefresh && (
                <button type="button" className={menuItemClass} onClick={() => { onRefresh(); setMenuOpen(false); }}>
                  <RefreshIcon className="w-4 h-4 mr-2" /> {t("Refresh")}
                </button>
              )}
              {itemId && (
                <CopyToClipboard text={itemId} onCopy={() => setMenuOpen(false)}>
                  <button type="button" className={menuItemClass}>
                    <ClipboardCopyIcon className="w-4 h-4 mr-2" /> {t("Copy Support Code")}
                  </button>
                </CopyToClipboard>
              )}
              {canRescind && onRescind && (
                <button type="button" className={menuItemClass} onClick={() => { onRescind(); setMenuOpen(false); }}>
                  <BanIcon className="w-4 h-4 mr-2" /> {t("Cancel Signature Requests")}
                </button>
              )}
              {canRerun && onRerun && (
                <button type="button" className={menuItemClass} onClick={() => { onRerun(); setMenuOpen(false); }}>
                  <PlayIcon className="w-4 h-4 mr-2" /> {t("Rerun")}
                </button>
              )}
              {canDelete && onDelete && (
                <button type="button" className={menuItemClass} onClick={() => { onDelete(); setMenuOpen(false); }}>
                  <TrashIcon className="w-4 h-4 mr-2" /> {t("Delete")}
                </button>
              )}
              {hubspotUrl && (
                <a
                  href={hubspotUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={menuItemClass}
                  onClick={() => setMenuOpen(false)}
                >
                  <ExternalLinkIcon className="w-4 h-4 mr-2" /> {t("HubSpot Deal")}
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
